import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch, Modal, Field } from "@/lib/ui";
import { PageHeader, Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wand2, X } from "lucide-react";

const EMPTY = { name: "", total: "", coefficient: 1, sub_subjects: [] };

function SubjectForm({ f, setF }) {
  const setSub = (i, patch) => setF({ ...f, sub_subjects: f.sub_subjects.map((s, x) => (x === i ? { ...s, ...patch } : s)) });
  return (
    <div className="space-y-3">
      <Field label="Nom de la matière"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="subject-name-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Total (ex. 300)"><Input type="number" value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} data-testid="subject-total-input" /></Field>
        <Field label="Coefficient"><Input type="number" step="0.5" value={f.coefficient} onChange={(e) => setF({ ...f, coefficient: e.target.value })} data-testid="subject-coef-input" /></Field>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium">Sous-matières</p>
        {f.sub_subjects.map((s, i) => (
          <div key={i} className="flex gap-2">
            <Input value={s.name} placeholder="Nom" onChange={(e) => setSub(i, { name: e.target.value })} data-testid={`subsubject-name-${i}`} />
            <Input className="w-28" type="number" value={s.total ?? ""} placeholder="Total" onChange={(e) => setSub(i, { total: e.target.value })} data-testid={`subsubject-total-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => setF({ ...f, sub_subjects: f.sub_subjects.filter((_, x) => x !== i) })} data-testid={`subsubject-remove-${i}`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
        {f.sub_subjects.some((s) => s.total !== "" && s.total != null) && <p className={`text-xs ${Math.abs(f.sub_subjects.reduce((a, s) => a + (parseFloat(s.total) || 0), 0) - (parseFloat(f.total) || 0)) > 0.01 ? "text-destructive font-semibold" : "text-muted-foreground"}`} data-testid="subsubject-sum">Somme des sous-matières : {f.sub_subjects.reduce((a, s) => a + (parseFloat(s.total) || 0), 0)} / total {f.total || 0}</p>}
        <Button variant="outline" size="sm" onClick={() => setF({ ...f, sub_subjects: [...f.sub_subjects, { name: "", total: "" }] })} data-testid="subsubject-add"><Plus className="h-4 w-4 mr-1" />Ajouter une sous-matière</Button>
      </div>
    </div>
  );
}

function UnmatchedExams({ classes, subjects, onFixed }) {
  const { data: rows, reload } = useFetch(() => api.get("/subjects/unmatched-exams"));
  const [pick, setPick] = useState({});
  const cname = (id) => (classes || []).find((c) => c.id === id)?.name || "—";
  const fix = async (e) => {
    const p = pick[e.id] || {};
    if (!p.subject_id) { toast.error("Choisissez une matière."); return; }
    try { await api.raw.patch(`/exams/${e.id}/subject`, { subject_id: p.subject_id, sub_subject: p.sub_subject || null }); toast.success("Examen associé"); reload(); onFixed?.(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const runAuto = async () => {
    try { const r = await api.post("/subjects/auto-match-exams"); toast.success(`${r.matched} examen(s) associé(s)`); reload(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  return (
    <div className="mt-8 rounded-lg border border-amber-300 bg-amber-50/40 p-4 space-y-3" data-testid="unmatched-exams">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading font-bold">Examens non associés ({(rows || []).length})</h3>
        <Button size="sm" variant="outline" onClick={runAuto} data-testid="auto-match-btn"><Wand2 className="h-4 w-4 mr-1" />Associer automatiquement</Button>
      </div>
      {(rows || []).length === 0 ? <p className="text-sm text-emerald-700" data-testid="unmatched-none">Tous les examens sont associés à une matière.</p> : (rows || []).map((e) => {
        const cands = (subjects || []).filter((s) => s.class_id === e.class_id);
        const p = pick[e.id] || {};
        const subs = cands.find((s) => s.id === p.subject_id)?.sub_subjects || [];
        return (
          <div key={e.id} className="flex flex-wrap items-center gap-2 border-t border-amber-200 pt-2 text-sm" data-testid={`unmatched-${e.id}`}>
            <div className="min-w-[200px] flex-1"><b>{e.title}</b><div className="text-xs text-muted-foreground">{cname(e.class_id)} · matière saisie : {e.subject || "—"}</div></div>
            <Select value={p.subject_id || ""} onValueChange={(v) => setPick({ ...pick, [e.id]: { subject_id: v } })}>
              <SelectTrigger className="w-48" data-testid={`unmatched-subject-${e.id}`}><SelectValue placeholder={cands.length ? "Matière" : "Aucune matière pour cette classe"} /></SelectTrigger>
              <SelectContent>{cands.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            {subs.length > 0 && (
              <Select value={p.sub_subject || ""} onValueChange={(v) => setPick({ ...pick, [e.id]: { ...p, sub_subject: v } })}>
                <SelectTrigger className="w-48" data-testid={`unmatched-sub-${e.id}`}><SelectValue placeholder="Sous-matière" /></SelectTrigger>
                <SelectContent>{subs.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button size="sm" onClick={() => fix(e)} data-testid={`unmatched-save-${e.id}`}>Associer</Button>
          </div>
        );
      })}
    </div>
  );
}

function UnmatchedGrades({ version }) {
  const { data: rows, reload } = useFetch(() => api.get("/subjects/unmatched-grades"));
  useEffect(() => { if (version) reload(); }, [version]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = rows || [];
  return (
    <div className="mt-6 rounded-lg border border-red-300 bg-red-50/40 p-4 space-y-2" data-testid="unmatched-grades">
      <h3 className="font-heading font-bold">Notes hors liste des matières ({list.length})</h3>
      <p className="text-xs text-muted-foreground">Ces notes ne comptent pas dans le total du bulletin. Ajoutez la matière (ou sous-matière) avec le même nom, ou corrigez la note.</p>
      {list.length === 0 ? <p className="text-sm text-emerald-700" data-testid="unmatched-grades-none">Toutes les notes correspondent à une matière.</p> : list.map((r, i) => (
        <div key={i} className="flex flex-wrap gap-2 border-t border-red-200 pt-1 text-sm" data-testid={`unmatched-grade-${i}`}>
          <b>{r.subject || "(sans matière)"}</b><span className="text-muted-foreground">· {r.class_name} · {r.term} · {r.count} note(s)</span>
        </div>
      ))}
    </div>
  );
}

export function SubjectsAdmin() {
  const { data: classes } = useFetch(() => api.get("/classes"));
  const { data: subjects, reload } = useFetch(() => api.get("/subjects"));
  const [cls, setCls] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [version, setVersion] = useState(0);
  const shown = (subjects || []).filter((s) => s.class_id === cls);

  const save = async () => {
    const payload = { ...f, class_id: cls, total: parseFloat(f.total) || 0, coefficient: parseFloat(f.coefficient) || 1,
      sub_subjects: f.sub_subjects.map((s) => ({ name: s.name, total: s.total === "" || s.total == null ? null : parseFloat(s.total) })) };
    try {
      if (editId) await api.put(`/subjects/${editId}`, payload); else await api.post("/subjects", payload);
      toast.success("Matière enregistrée"); setOpen(false); reload(); setVersion((v) => v + 1);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Supprimer la matière « ${s.name} » ? Les examens liés devront être réassociés.`)) return;
    try { await api.del(`/subjects/${s.id}`); toast.success("Matière supprimée"); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const seed = async () => {
    try { const r = await api.post("/subjects/seed"); toast.success(`${r.created} matière(s) créée(s) depuis les bulletins`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Matières" description="Liste centrale des matières par classe : total, sous-matières et coefficient." />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={cls} onValueChange={setCls}>
          <SelectTrigger className="w-64" data-testid="subjects-class-select"><SelectValue placeholder="Choisir une classe" /></SelectTrigger>
          <SelectContent>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.grade_level}</SelectItem>)}</SelectContent>
        </Select>
        <Button disabled={!cls} onClick={() => { setF(EMPTY); setEditId(null); setOpen(true); }} data-testid="subject-add-btn"><Plus className="h-4 w-4 mr-1" />Nouvelle matière</Button>
        <Button variant="outline" onClick={seed} data-testid="subjects-seed-btn"><Wand2 className="h-4 w-4 mr-1" />Préremplir les classes sans matières</Button>
      </div>
      {!cls ? <Empty title="Choisissez une classe" hint="Les matières sont propres à chaque classe." /> : shown.length === 0 ? <Empty title="Aucune matière pour cette classe" /> : (
        <div className="space-y-2" data-testid="subjects-list">
          {shown.map((s) => (
            <div key={s.id} className="rounded-md border border-border bg-card p-3 flex flex-wrap items-start justify-between gap-2" data-testid={`subject-row-${s.id}`}>
              <div>
                <p className="font-semibold">{s.name} <span className="text-xs text-muted-foreground">· Total {s.total} · Coef. {s.coefficient}</span></p>
                {(s.sub_subjects || []).length > 0 && <p className="text-xs text-muted-foreground">{s.sub_subjects.map((x) => `${x.name}${x.total != null ? ` (${x.total})` : ""}`).join(" · ")}</p>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => { setF({ ...EMPTY, ...s, sub_subjects: (s.sub_subjects || []).map((x) => ({ ...x })) }); setEditId(s.id); setOpen(true); }} data-testid={`subject-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(s)} data-testid={`subject-delete-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <UnmatchedExams classes={classes} subjects={subjects} />
      <UnmatchedGrades version={version} />
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier la matière" : "Nouvelle matière"} footer={<Button onClick={save} data-testid="subject-save-btn">Enregistrer</Button>}>
        <SubjectForm f={f} setF={setF} />
      </Modal>
    </div>
  );
}
